import React, { createContext, useContext, useEffect, useState } from 'react';

import { useLocation } from 'react-router-dom';

import InitializationLoader from 'components/InitializationLoader';
import { useUser } from 'global/UserContext';
import { useInitializationProgress } from 'hooks/useInitializationProgress';
import { isPublicRoute as checkIsPublicRoute } from 'routes/PublicRoutes';
import { logger } from 'utils/logger';

interface InitializationContextType {
  isInitializing: boolean;
  hasInitialized: boolean;
  shouldShowLoader: boolean;
}

const InitializationContext = createContext<InitializationContextType | undefined>(undefined);

interface InitializationProviderProps {
  children: React.ReactNode;
}

/**
 * Centralized provider that manages the initialization loading
 * Prevents multiple AppLoading instances and maintains a single fixed component
 */
export const InitializationProvider: React.FC<InitializationProviderProps> = ({ children }) => {
  const { isInitializing, hasInitialized } = useInitializationProgress();
  const { token, user } = useUser();
  const location = useLocation();
  const [hasEverInitialized, setHasEverInitialized] = useState(false);

  // Check if current route is public (includes dynamic routes like /invite/event/:uuid)
  const isPublicRoute = checkIsPublicRoute(location.pathname);

  // FIX: Initialize shouldShowLoader based on whether the route is public
  // For public routes (like /login), we should NEVER show the initialization loader
  // This prevents the infinite loading bug on first access
  const [shouldShowLoader, setShouldShowLoader] = useState(() => {
    // On initial render, if it's a public route, don't show loader
    return !checkIsPublicRoute(window.location.pathname);
  });

  // Effect 1: Determine loader visibility based on auth state and route
  useEffect(() => {
    if (isPublicRoute) {
      // On /login with real auth transition, show loader
      if (location.pathname === '/login' && isInitializing && !hasEverInitialized && token && user) {
        logger.debug('[InitializationProvider] Login with real authentication, showing loader');
        setShouldShowLoader(true);
        return;
      }
      setShouldShowLoader(false);
      return;
    }

    // Protected routes: show loader only during first initialization
    if (!hasEverInitialized && isInitializing) {
      setShouldShowLoader(true);
    }
  }, [isPublicRoute, isInitializing, hasEverInitialized, location.pathname, token, user]);

  // Effect 2: When initialization completes, transition loader out
  useEffect(() => {
    if (hasInitialized && !hasEverInitialized) {
      logger.debug('[InitializationProvider] Initialization complete, hiding loader...');
      setHasEverInitialized(true);
      const timer = setTimeout(() => {
        setShouldShowLoader(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [hasInitialized, hasEverInitialized]);

  const contextValue: InitializationContextType = {
    isInitializing,
    hasInitialized,
    shouldShowLoader,
  };

  return (
    <InitializationContext.Provider value={contextValue}>
      {shouldShowLoader && <InitializationLoader />}
      {/* Always render children, even during loading */}
      {children}
    </InitializationContext.Provider>
  );
};

export const useInitialization = (): InitializationContextType => {
  const context = useContext(InitializationContext);
  if (!context) {
    throw new Error('useInitialization must be used within an InitializationProvider');
  }
  return context;
};
